import React from "react";
import { fmtKg, fmtEur, fmtPct, SELL, labelOf, DISPOSALS } from "./prodApi";
import { PCard, PNum, ReconcileBar, PBadge } from "./ProdUi";

const Row = ({ label, value, strong, tone, testId }) => (
  <div className={`flex justify-between gap-3 py-1.5 ${strong ? "border-t border-slate-600 mt-1 pt-2" : ""}`} data-testid={testId}>
    <span className={`text-sm ${strong ? "text-white font-bold" : "text-slate-400"}`}>{label}</span>
    <span className={`font-['Chivo'] tabular-nums ${strong ? "font-black text-white" : "font-bold text-slate-200"} ${tone || ""}`}>{value}</span>
  </div>
);

const Delta = ({ v, fmt = (x) => fmtKg(x, 2), suffix = "" }) => {
  if (v == null) return <span className="text-slate-500">—</span>;
  const tone = Math.abs(v) < 0.0005 ? "text-slate-400" : v > 0 ? "text-emerald-400" : "text-red-400";
  return <span className={`tabular-nums ${tone}`}>{v > 0 ? "+" : ""}{fmt(v)}{suffix}</span>;
};

export default function ProdResult({ result, prices, onPriceChange }) {
  if (!result) return null;
  const { mass, costs, revenue, result: res, rows, comparison, input } = result;
  const sell = rows.filter((r) => SELL.includes(r.disposal));
  const others = rows.filter((r) => !SELL.includes(r.disposal));
  const negative = res.margin < 0;
  const cmp = !!comparison;

  return (
    <div className="space-y-4" data-testid="prod-result">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          ["Fatturato", fmtEur(revenue.sales), `${fmtEur(revenue.avg_price_per_kg)}/kg medio`, "text-white", "prod-res-revenue"],
          ["Costo totale", fmtEur(costs.total), `materia ${fmtEur(costs.input)} + lavorazione ${fmtEur(costs.processing)}`, "text-white", "prod-res-cost"],
          ["Margine", fmtEur(res.margin), fmtPct(res.margin_pct) + " sul fatturato", negative ? "text-red-400" : "text-emerald-400", "prod-res-margin"],
          ["Margine €/kg", fmtEur(res.margin_per_kg_input), `${fmtEur(res.margin_per_kg_sold)} per kg venduto`, negative ? "text-red-400" : "text-emerald-400", "prod-res-margin-kg"],
          ["Margine €/ora", res.margin_per_hour == null ? "—" : fmtEur(res.margin_per_hour), `${fmtKg(costs.labour_hours, 2)} h · ${fmtEur(res.margin_per_piece)} per pezzo`, negative ? "text-red-400" : "text-emerald-400", "prod-res-margin-hour"],
        ].map(([l, v, s, c, tid]) => (
          <PCard key={l} className="!p-4" data-testid={tid}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{l}</p>
            <p className={`font-['Chivo'] font-black text-xl sm:text-2xl tabular-nums mt-1 ${c}`}>{v}</p>
            <p className="text-[11px] text-slate-500 mt-1">{s}</p>
          </PCard>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <PCard data-testid="prod-res-mass">
          <h3 className="font-['Chivo'] font-black text-white mb-2">Bilancio di massa</h3>
          <Row label="Input" value={`${fmtKg(mass.input_kg, 2)} kg`} />
          <Row label={`Vendibile · ${fmtPct(mass.sellable_yield_pct)}`} value={`${fmtKg(mass.sellable_kg, 2)} kg`} tone="text-emerald-300" />
          <Row label={`Sottoprodotti · ${fmtPct(mass.byproduct_pct)}`} value={`${fmtKg(mass.byproduct_kg, 2)} kg`} />
          <Row label={`Scarto · ${fmtPct(mass.waste_pct)}`} value={`${fmtKg(mass.waste_kg, 2)} kg`} tone="text-red-300" />
          <Row label="Non riconciliato / calo" value={`${fmtKg(mass.unreconciled_kg, 3)} kg`} tone={mass.reconciled ? "text-slate-400" : "text-amber-300"} testId="prod-res-unreconciled" />
          <div className="mt-3"><ReconcileBar input={mass.input_kg} total={mass.output_kg} testId="prod-res-reconcile-bar" /></div>
        </PCard>
        <PCard data-testid="prod-res-costs">
          <h3 className="font-['Chivo'] font-black text-white mb-2">Bilancio dei costi</h3>
          <Row label={`Materia prima · ${fmtEur(input.price_per_kg_effective)}/kg`} value={fmtEur(costs.input)} />
          <Row label={`Manodopera · ${fmtKg(costs.labour_hours, 2)} h`} value={fmtEur(costs.labour)} />
          <Row label="Materiali" value={fmtEur(costs.materials)} />
          <Row label="Macchine / altri" value={fmtEur(costs.other)} />
          <Row label="Smaltimento scarti" value={fmtEur(costs.disposal)} />
          <Row label="− Ricavo sottoprodotti" value={`− ${fmtEur(costs.byproduct_revenue)}`} tone="text-emerald-300" />
          <Row label="Costo da distribuire" value={fmtEur(costs.to_distribute)} strong testId="prod-res-to-distribute" />
          <p className="text-xs text-slate-500 mt-2">Costo medio {fmtEur(costs.avg_cost_per_sellable_kg)}/kg vendibile · lavorazione {fmtEur(costs.processing_per_kg_input)}/kg input</p>
        </PCard>
        <PCard data-testid="prod-res-breakeven">
          <h3 className="font-['Chivo'] font-black text-white mb-2">Calcolo inverso</h3>
          <p className="text-xs text-slate-400">Con questi prezzi di vendita e queste rese:</p>
          <Row label={`Prezzo max acquisto per margine ${fmtPct(res.target_margin_pct, 0)}`} value={res.max_input_price_for_target == null ? "—" : `${fmtEur(res.max_input_price_for_target)}/kg`} tone="text-blue-300" testId="prod-res-max-price" />
          <Row label="Prezzo di pareggio (margine 0)" value={`${fmtEur(res.breakeven_input_price)}/kg`} testId="prod-res-breakeven-price" />
          <Row label="Prezzo pagato" value={`${fmtEur(input.price_per_kg_effective)}/kg`} tone={input.price_per_kg_effective > (res.max_input_price_for_target ?? Infinity) ? "text-amber-300" : "text-emerald-300"} />
          {cmp && <>
            <Row label="Resa vendibile teorica → reale" value={`${fmtPct(comparison.yield_theoretical_pct)} → ${fmtPct(comparison.yield_real_pct)}`} strong />
            <Row label="Δ kg vendibili" value={<Delta v={comparison.diff_sellable_kg} suffix=" kg" />} testId="prod-res-diff-kg" />
            <Row label="Δ valore economico" value={<Delta v={comparison.diff_value_total} fmt={(x) => fmtEur(x)} />} testId="prod-res-diff-value" />
          </>}
        </PCard>
      </div>

      <PCard className="!p-0 overflow-hidden" data-testid="prod-res-rows">
        <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
          <h3 className="font-['Chivo'] font-black text-white">Output · metodo {labelOf([{ value: "average", label: "costo medio" }, { value: "weighted", label: "coefficienti" }, { value: "nrv", label: "valore di vendita" }], result.method)}</h3>
          <span className="text-xs text-slate-500">{sell.length} vendibili · {others.length} sottoprodotti/scarti</span>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-slate-400 bg-slate-900/60">
              <tr>
                <th className="text-left px-4 py-2">Taglio</th>
                {cmp && <th className="text-right px-2 py-2">Kg teorici</th>}
                <th className="text-right px-2 py-2">Kg</th>
                <th className="text-right px-2 py-2">Resa</th>
                {cmp && <th className="text-right px-2 py-2">Δ kg</th>}
                {cmp && <th className="text-right px-2 py-2">Δ pt</th>}
                <th className="text-right px-2 py-2">Coeff.</th>
                <th className="text-right px-2 py-2">Costo €/kg</th>
                <th className="text-right px-2 py-2">Vendita €/kg</th>
                <th className="text-right px-2 py-2">Ricavo</th>
                <th className="text-right px-2 py-2">Margine</th>
                <th className="text-right px-4 py-2">Margine %</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {sell.map((r) => (
                <tr key={r.key} className="border-t border-slate-700/60" data-testid={`prod-res-row-${r.key}`}>
                  <td className="px-4 py-2 text-white font-semibold">{r.node_name}{r.trim_level_name && <span className="text-slate-500 font-normal"> · {r.trim_level_name}</span>}</td>
                  {cmp && <td className="text-right px-2 py-2 text-slate-400">{fmtKg(r.kg_theoretical, 2)}</td>}
                  <td className="text-right px-2 py-2 text-white">{fmtKg(r.kg, 2)}</td>
                  <td className="text-right px-2 py-2 text-slate-300">{fmtPct(r.pct, 2)}</td>
                  {cmp && <td className="text-right px-2 py-2"><Delta v={r.diff_kg} /></td>}
                  {cmp && <td className="text-right px-2 py-2"><Delta v={r.diff_pct_points} /></td>}
                  <td className="text-right px-2 py-2 text-slate-300">{r.coefficient}</td>
                  <td className="text-right px-2 py-2 text-slate-200">{fmtEur(r.cost_per_kg)}</td>
                  <td className="text-right px-2 py-2">
                    {onPriceChange ? <div className="w-28 ml-auto"><PNum value={prices?.[r.key] ?? String(r.sale_price_per_kg)} onChange={(e) => onPriceChange(r.key, e.target.value)} className="!h-11 !text-base" data-testid={`prod-res-price-${r.key}`} /></div> : <span className="text-slate-200">{fmtEur(r.sale_price_per_kg)}</span>}
                  </td>
                  <td className="text-right px-2 py-2 text-white">{fmtEur(r.revenue)}</td>
                  <td className={`text-right px-2 py-2 font-bold ${r.margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtEur(r.margin)}</td>
                  <td className={`text-right px-4 py-2 ${r.margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtPct(r.margin_pct)}</td>
                </tr>
              ))}
              {others.map((r) => (
                <tr key={r.key} className="border-t border-slate-700/60 bg-slate-900/40" data-testid={`prod-res-row-${r.key}`}>
                  <td className="px-4 py-2 text-slate-300">{r.node_name} <PBadge tone={r.value_per_kg < 0 ? "red" : "amber"} className="ml-1">{labelOf(DISPOSALS, r.disposal)}</PBadge></td>
                  {cmp && <td className="text-right px-2 py-2 text-slate-500">{fmtKg(r.kg_theoretical, 2)}</td>}
                  <td className="text-right px-2 py-2 text-slate-200">{fmtKg(r.kg, 2)}</td>
                  <td className="text-right px-2 py-2 text-slate-400">{fmtPct(r.pct, 2)}</td>
                  {cmp && <td className="text-right px-2 py-2"><Delta v={r.diff_kg} /></td>}
                  {cmp && <td className="text-right px-2 py-2"><Delta v={r.diff_pct_points} /></td>}
                  <td className="text-right px-2 py-2 text-slate-600">—</td>
                  <td className="text-right px-2 py-2 text-slate-600">—</td>
                  <td className="text-right px-2 py-2 text-slate-400">{fmtEur(r.value_per_kg)}</td>
                  <td className={`text-right px-2 py-2 ${r.value_total < 0 ? "text-red-300" : "text-emerald-300"}`} colSpan={3}>{fmtEur(r.value_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden divide-y divide-slate-700/60">
          {rows.map((r) => {
            const isSell = SELL.includes(r.disposal);
            return (
              <div key={r.key} className="p-4" data-testid={`prod-res-card-${r.key}`}>
                <div className="flex justify-between items-start gap-2">
                  <p className="font-semibold text-white">{r.node_name}{r.trim_level_name && <span className="text-slate-500 text-xs"> · {r.trim_level_name}</span>}</p>
                  <p className="font-['Chivo'] font-black text-lg text-white tabular-nums">{fmtKg(r.kg, 2)} kg</p>
                </div>
                <p className="text-xs text-slate-400 tabular-nums">resa {fmtPct(r.pct, 2)}{cmp && <> · teorica {fmtKg(r.kg_theoretical, 2)} kg · <Delta v={r.diff_kg} suffix=" kg" /></>}</p>
                {isSell ? (
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    <div><p className="text-slate-500">Costo €/kg</p><p className="text-slate-200 tabular-nums font-bold">{fmtEur(r.cost_per_kg)}</p></div>
                    <div><p className="text-slate-500">Vendita €/kg</p>{onPriceChange ? <PNum value={prices?.[r.key] ?? String(r.sale_price_per_kg)} onChange={(e) => onPriceChange(r.key, e.target.value)} className="!h-11 !text-base" data-testid={`prod-res-price-m-${r.key}`} /> : <p className="text-slate-200 tabular-nums font-bold">{fmtEur(r.sale_price_per_kg)}</p>}</div>
                    <div><p className="text-slate-500">Margine</p><p className={`tabular-nums font-bold ${r.margin < 0 ? "text-red-400" : "text-emerald-400"}`}>{fmtEur(r.margin)}<span className="block text-[10px] font-normal">{fmtPct(r.margin_pct)}</span></p></div>
                  </div>
                ) : (
                  <p className="text-xs mt-1"><PBadge tone={r.value_per_kg < 0 ? "red" : "amber"}>{labelOf(DISPOSALS, r.disposal)}</PBadge> <span className={`ml-2 tabular-nums ${r.value_total < 0 ? "text-red-300" : "text-emerald-300"}`}>{fmtEur(r.value_per_kg)}/kg → {fmtEur(r.value_total)}</span></p>
                )}
              </div>
            );
          })}
        </div>
      </PCard>
    </div>
  );
}
